import { useState } from "react";
import { fetchBlobSasToken } from '../../../api/internal';
import { JobResult, JobResultV2, JobResultV2_Finding, OnDelete } from '../../../types/job';
import { ReferencePopup } from "../../conversations/reference-popup";
import { createPortal } from "react-dom";
import { File } from '../../../types/file';
import ExcelJS from 'exceljs';

interface JobResultElProps {
  result: JobResult<JobResultV2>;
  finding: JobResultV2_Finding;
  file?: File;
  isEvenRow: boolean;
  isSelected: boolean;
  onSelect: (checked: boolean) => void;
  onDelete: OnDelete;
  comment: string;
  onCommentChange: (comment: string) => void;
}

const JobResultEl: React.FC<JobResultElProps> = ({
  result,
  finding,
  file,
  isEvenRow,
  isSelected,
  onSelect,
  comment,
  onCommentChange,
}) => {
  const [showReference, setShowReference] = useState(false);
  const [sasToken, setSasToken] = useState<string>('');

  const showReferencePopup = async () => {
    if (!file || !result) return;
    const token = await fetchBlobSasToken(result.topicId, file.file);
    setSasToken(token);
    setShowReference(true);
  };

  const sanitizeHtml = (html: string): string => {
    return html.replace(/\s*<(\/)?(b|strong)>\s*/g, '<$1$2>');
  };

  const sanitizedClause = { __html: sanitizeHtml(finding.clause) };
  const evenOddClass = isEvenRow ? 'bg-gray-50' : 'bg-white';

  return (
    <div className={`flex flex-row items-stretch ${evenOddClass} border-b border-gray-300 pb-2`}>
      <div className="flex-0 w-12 flex items-center justify-center">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={(e) => onSelect(e.target.checked)}
          className="w-4 h-4 cursor-pointer"
        />
      </div>
      <div className="flex-0 w-12 flex items-center justify-center">
        <button className="text-blue-500 hover:underline" onClick={showReferencePopup}>
          {result.page}
        </button>
      </div>
      <div className="flex-0 w-48 pr-4 flex items-center">
        <pre className="overflow-hidden">{result.file.metadata?.title || ''}</pre>
      </div>
      <div className="flex-0 w-36 flex items-center">
        <pre className="whitespace-pre-wrap">{finding.clause_address}</pre>
      </div>
      <div className="flex-1 flex items-center">
        <span 
          className="whitespace-pre-wrap" 
          dangerouslySetInnerHTML={sanitizedClause} 
        />
      </div>
      <div className="flex-1 flex items-center px-2">
        <textarea
          value={comment}
          onChange={(e) => onCommentChange(e.target.value)}
          className="border border-gray-300 rounded px-2 py-1 w-full resize-none"
          rows={3}
          placeholder="Add comment..."
        />
      </div>
      {showReference && file && createPortal(
        <ReferencePopup 
          path={`${file.file}?${sasToken}`} 
          page={result.page.toFixed(0)} 
          onClose={() => setShowReference(false)} 
        />,
        document.body
      )}
    </div>
  );
};

interface JobResultsRendererV2Props {
  results: JobResult<JobResultV2>[];
  files: File[];
  onDelete: OnDelete;
  onDownloadSelected?: (selectedResults: JobResult<JobResultV2>[]) => void;
}

export const JobResultsRendererV2: React.FC<JobResultsRendererV2Props> = ({
  results,
  files,
  onDelete,
}) => {
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [comments, setComments] = useState<Record<number, string>>({});

  const handleCommentChange = (index: number, value: string) => {
    setComments(prev => ({ ...prev, [index]: value }));
  };

  // Remove duplicates based on page, clause, and text
  const deduplicatedResults = Array.from(
    new Map(
      results
        .filter(result => result.result.findings?.length)
        .flatMap(result => result.result.findings.map(finding => ({ finding, result })))
        .map(r => [
          `${r.result.page}-${r.finding.clause_address}-${r.finding.clause}`,
          r,
        ])
    ).values()
  );

  const sortedResults = deduplicatedResults
    .sort((a, b) => a.result.page - b.result.page || a.result.file.filename.localeCompare(b.result.file.filename));

  const handleSelectAll = (checked: boolean) => {
    setSelectedRows(checked ? new Set(sortedResults.map((_, i) => i)) : new Set());
  };

  const removeHtmlTags = (html: string): string => html.replace(/<[^>]*>/g, '');

  const downloadExcel = async (selectedResults: { finding: JobResultV2_Finding, result: JobResult<JobResultV2> }[]) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Selected Results');
    
    // Add headers
    worksheet.addRow([
      'S.No', 'Page', 'Spec No.', 'Rev.', 'Title', 'Clause', 'Text', 'D/C/CL', 'Comment'
    ]);

    // Add data rows with correct comment mapping
    const rows = selectedResults.map(({ finding, result }, exportIndex) => {
      // Find the original index in sortedResults
      const originalIndex = sortedResults.findIndex(r => 
        r.result.page === result.page && 
        r.finding.clause_address === finding.clause_address &&
        r.finding.clause === finding.clause
      );
      
      return {
        "S.No": exportIndex + 1,
        "Page": result.page,
        "Spec No.": result.file.metadata?.specification_id || "",
        "Rev.": result.file.metadata?.revision || "",
        "Title": result.file.metadata?.title || "",
        "Clause": finding.clause_address || "?",
        "Text": removeHtmlTags(finding.clause),
        "D/C/CL": "",
        "Comment": originalIndex >= 0 ? comments[originalIndex] || "" : "" // Correct comment lookup
      };
    });

    // Insert empty rows at the top
    const numRowsToShift = 7;
    for (let i = 0; i < numRowsToShift; i++) {
      worksheet.insertRow(1, []);
    }

    // Add rows to the worksheet
    rows.forEach(row => {
      worksheet.addRow(Object.values(row));
    });

    // Merge cells B2:I5
    worksheet.mergeCells('B2:I5');
    worksheet.getCell("B2").border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    };

    // Merge cells B6:H6 and insert text "Quotation Reference:"
    worksheet.mergeCells('B6:H6');
    worksheet.getCell('B6').value = 'Quotation Reference:';
    worksheet.getCell('B6').alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
    worksheet.getCell('B6').font = { bold: true };
    worksheet.getCell("B6").border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    };

    // Merge cells B7:H7 and insert text "Revision:"
    worksheet.mergeCells('B7:H7');
    worksheet.getCell('B7').value = 'Revision:';
    worksheet.getCell('B7').alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
    worksheet.getCell('B7').font = { bold: true };
    worksheet.getCell("B7").border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    };

    // Insert "???" in I6 and I7
    worksheet.getCell('I6').value = '???';
    worksheet.getCell('I6').alignment = { vertical: 'middle', horizontal: 'left' };
    worksheet.getCell("I6").border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    };

    worksheet.getCell('I7').value = '???';
    worksheet.getCell('I7').alignment = { vertical: 'middle', horizontal: 'left' };
    worksheet.getCell("I7").border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    };

    // Format row 8: fill with #C0C0C0 and make text bold
    const headerRow = worksheet.getRow(8);
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFC0C0C0' },
      };
      cell.font = { bold: true };
      cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
    });

    // Set column widths
    worksheet.columns = [
      { key: 'S.No', width: 5 },
      { key: 'Page', width: 5 },
      { key: 'Spec No.', width: 10 },
      { key: 'Rev.', width: 5 },
      { key: 'Title', width: 20 },
      { key: 'Clause', width: 10 },
      { key: 'Text', width: 60 },
      { key: 'D/C/CL', width: 10 },
      { key: 'Comment', width: 30 },
    ];

    // Apply text wrapping, alignment, and borders to cells from row 8 and below
    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber >= 8) {
        row.eachCell((cell) => {
          cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' },
          };
        });
      }
    });

    // Save the workbook
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'selected_results.xlsx';
    link.click();
  };

  return (
    <div className="flex flex-col result-v2">
      <div className="flex justify-between items-center mb-4">
        {selectedRows.size > 0 && (
          <button
            onClick={() => {
              const selectedResults = sortedResults
                .filter((_, i) => selectedRows.has(i));
              downloadExcel(selectedResults);
            }}
            className="flex items-center gap-2 px-4 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Download Selected ({selectedRows.size})
          </button>
        )}
      </div>

      <div className="flex flex-row font-bold border-b border-gray-300 pb-2">
        <div className="flex-0 w-12 flex items-center justify-center">
          <input
            type="checkbox"
            checked={selectedRows.size > 0 && selectedRows.size === sortedResults.length}
            onChange={(e) => handleSelectAll(e.target.checked)}
            className="w-4 h-4 cursor-pointer"
          />
        </div>
        <div className="flex-0 w-12 text-center">Page</div>
        <div className="flex-0 w-48 pr-4">Title</div>
        <div className="flex-0 w-36">Clause</div>
        <div className="flex-1">Text</div>
        <div className="flex-1 px-2">Comment</div>
      </div>

      {sortedResults.map((r, i) => (
        <JobResultEl
          key={i}
          isEvenRow={i % 2 === 0}
          result={r.result}
          finding={r.finding}
          file={files[0]}
          onDelete={onDelete}
          isSelected={selectedRows.has(i)}
          onSelect={(checked) => {
            const newSelectedRows = new Set(selectedRows);
            checked ? newSelectedRows.add(i) : newSelectedRows.delete(i);
            setSelectedRows(newSelectedRows);
          }}
          comment={comments[i] || ''}
          onCommentChange={(comment) => handleCommentChange(i, comment)}
        />
      ))}
    </div>
  );
};